import ComposingState from '../cache/composing';
import CandidateCache from '../cache/candidates';
import { isAccentKey, isSpecialKey } from '../utils/KeyUtils';
import { CandidatesKeyboard, KeyboardModule, KeyboardOptionsExt } from 'components/Keyboard/types';
import { SPECIAL_KEYS } from '../utils/constants';

export type CandidateProvider = (input: string, maxResults?: number, keyboard?: CandidatesKeyboard) => Promise<string[]>;

const MAX_CANDIDATE_RESULTS = 20;

const EXCLUDED_CANDIDATE_INPUT_TYPES: HTMLInputElement['type'][] = ['email', 'tel', 'url',];

// react-simple-keyboard calls keyboard.options.onChange internally from its own handler
// there is no hook to intercept it. The only way to replace the emitted value is to
// temporarily swap onChange with a no-op before the library call, then emit the desired
// value ourselves once the library is done.
const callWithFormattedOnChange = (
  keyboard: CandidatesKeyboard,
  libraryCall: () => void,
  buildFormattedValue: () => string,
) => {
  const savedOnChange = keyboard.options.onChange;
  keyboard.options.onChange = () => {};
  libraryCall();
  keyboard.options.onChange = savedOnChange;
  savedOnChange?.(buildFormattedValue());
};

const candidatesModule = (
  candidateProvider: CandidateProvider,
  formatComposing?: (s: string) => string,
  isComposingKey: (button: string) => boolean = () => true,
) => function candidates(keyboard: CandidatesKeyboard) {
  const enableCache = false;
  let currentHighlightPage = 0;
  let currentCandidates: string[] = [];
  let selectedCandidateIndex = -1;

  const setInputRange = (input: string) => {
    const { enableLayoutCandidates } = keyboard.getOptions();
    if (enableLayoutCandidates) {
      const composing = ComposingState.get();
      const start = input.lastIndexOf(composing);

      if (start !== -1) {
        const displayComposing = formatComposing && composing ? formatComposing(composing) : composing;
        keyboard.options.onSelectionChange?.([start, start + displayComposing.length]);
      }
    }
  };

  const applyCandidate = (candidate: string, backtrackLength: number) => {
    const input = keyboard.getInput();
    const newInput = input.slice(0, -backtrackLength) + candidate;
    keyboard.setInput(newInput);
    keyboard.setCaretPosition(newInput.length);
    keyboard.options.onChange?.(newInput);
  };

  const onSelectCandidate = (button: string) => (selectedCandidate: string, _event: MouseEvent) => {
    flushCandidates();
    applyCandidate(selectedCandidate, button.length);
  };

  const refreshCandidates = () => {
    const composing = ComposingState.get();
    if (composing) {
      updateCandidates(composing);
      setInputRange(keyboard.getInput());
    }
  };

  const flushCandidates = () => {
    const hadCandidates = currentCandidates.length > 0;
    ComposingState.reset();
    currentCandidates = [];
    selectedCandidateIndex = -1;
    currentHighlightPage = 0;
    setInputRange(keyboard.getInput());
    keyboard.setOptions({ layoutCandidates: {} });
    keyboard.candidateBox?.destroy();
    if (hadCandidates) {
      keyboard.onCandidatesChange?.(false);
    }
  };

  const updateCandidates = async (button: string) => {
    const { enableLayoutCandidates } = keyboard.getOptions();
    if (button && enableLayoutCandidates) {
      let candidates = CandidateCache.get(button);
      if (!enableCache || !candidates || isAccentKey(button) ) {
        // Fetch new candidates from provider if composing state it's not in cache
        const newCandidates = (await candidateProvider(button, MAX_CANDIDATE_RESULTS, keyboard)) ?? [];
        if (newCandidates.length > 0) {
          CandidateCache.clear(button);
          CandidateCache.set(button, newCandidates);
          candidates = CandidateCache.get(button);
        }
      }

      // Set keyboard candidates + show candidates box
      const hadCandidates = currentCandidates.length > 0;
      currentCandidates = candidates ? candidates.split(' ') : [];
      selectedCandidateIndex = -1;
      currentHighlightPage = 0;
      keyboard.setOptions({ layoutCandidates: CandidateCache.getAll() });
      requestAnimationFrame(() => {
        if (keyboard.candidateBox && candidates) {
          keyboard.candidateBox.show({
            candidateValue: candidates,
            targetElement: keyboard.keyboardDOM,
            onSelect: onSelectCandidate(button),
          });
        }
      });
      if (!hadCandidates && currentCandidates.length > 0) {
        keyboard.onCandidatesChange?.(true);
      }
    } else {
      // No input, clear candidates
      flushCandidates();
    }
  };

  const highlightCandidate = (index: number) => {
    if (!keyboard.candidateBox?.candidateBoxElement) {
      return;
    }

    const pageSize = keyboard.candidateBox?.pageSize || currentCandidates.length;
    const targetPage = Math.floor(index / pageSize);

    if (currentHighlightPage < targetPage) {
      keyboard.candidateBox.candidateBoxElement
        ?.querySelector('.hg-candidate-box-next')?.dispatchEvent(new MouseEvent('click'));
      currentHighlightPage++;
    }

    const indexInPage = index % pageSize;
    const container = keyboard.candidateBox.candidateBoxElement;
    container?.querySelector('.hg-candidate-box-list-item.selected')?.classList.remove('selected');
    container?.querySelectorAll('.hg-candidate-box-list-item')[indexInPage]?.classList.add('selected');
  };

  const selectNextCandidate = (): string | null => {
    if (currentCandidates.length === 0) {
      return null;
    }
    if (selectedCandidateIndex < currentCandidates.length - 1) {
      selectedCandidateIndex++;
    }
    highlightCandidate(selectedCandidateIndex);
    return currentCandidates[selectedCandidateIndex];
  };

  const confirmSelectedCandidate = () => {
    if (selectedCandidateIndex < 0 || !currentCandidates[selectedCandidateIndex]) {
      return;
    }
    const selected = currentCandidates[selectedCandidateIndex];
    const composing = ComposingState.get();
    flushCandidates();
    applyCandidate(selected, composing.length);
  };

  const confirmFirstCandidate = (): boolean => {
    if (currentCandidates.length === 0) {
      return false;
    }
    const first = currentCandidates[0];
    const composing = ComposingState.get();
    flushCandidates();
    applyCandidate(first, composing.length);
    return true;
  };

  // Provide candidate functions to keyboard for other modules
  keyboard.updateCandidates = updateCandidates;
  keyboard.flushCandidates = flushCandidates;
  keyboard.setInputRange = setInputRange;
  keyboard.refreshCandidates = refreshCandidates;
  keyboard.selectNextCandidate = selectNextCandidate;
  keyboard.confirmSelectedCandidate = confirmSelectedCandidate;
  keyboard.confirmFirstCandidate = confirmFirstCandidate;

  keyboard.registerModule('candidates', (module: KeyboardModule) => {
    module.fn = {
      handleButtonClicked: keyboard.handleButtonClicked,
      clearInput: keyboard.clearInput,
    };

    // Disable candidates if input type is excluded
    const { type: inputType } = keyboard.getOptions() as KeyboardOptionsExt;
    if (EXCLUDED_CANDIDATE_INPUT_TYPES.includes(inputType)) {
      keyboard.setOptions({
        enableLayoutCandidates: false,
      });
    }

    // Init default candidates to cache and reset composing state
    ComposingState.reset();
    keyboard.options.onSelectionChange?.([0, 0]);
    CandidateCache.setCandidates(keyboard.getOptions().layoutCandidates);

    keyboard.handleButtonClicked = (...args) => {
      const [button] = args;
      const { enableLayoutCandidates } = keyboard.getOptions();
      const composing = ComposingState.get();

      if (enableLayoutCandidates) {
        if (button === SPECIAL_KEYS.CONFIRM || button === SPECIAL_KEYS.SPACE) {
          flushCandidates();
        }

        if (button === SPECIAL_KEYS.BACKSPACE) {
          ComposingState.set(composing.slice(0, -1));
          setInputRange(keyboard.getInput().slice(0, -1));
          updateCandidates(ComposingState.get());
        }

        if (!isSpecialKey(button)) {
          if (isComposingKey(button)) {
            ComposingState.set(composing + button);
            updateCandidates(ComposingState.get());
            setInputRange(keyboard.getInput() + button);
          } else if (composing) {
            if (!confirmFirstCandidate()) {
              flushCandidates();
            }
          }
        }
      }
      const newComposing = ComposingState.get();
      const affectsComposing = !isSpecialKey(button) || button === SPECIAL_KEYS.BACKSPACE;
      const shouldFormatOnChange = formatComposing && enableLayoutCandidates && newComposing && affectsComposing;

      if (shouldFormatOnChange) {
        callWithFormattedOnChange(
          keyboard,
          () => module.fn.handleButtonClicked.call(keyboard, button),
          () => {
            const rawInput = keyboard.getInput();
            const prefix = rawInput.slice(0, rawInput.length - newComposing.length);
            return prefix + formatComposing(newComposing);
          },
        );
      } else {
        module.fn.handleButtonClicked.call(keyboard, button);
      }

      // react-simple-keyboard destroys candidateBox when layoutName changes via setOptions.
      // Re-show candidates if shift toggled the layout while composing.
      if (button === SPECIAL_KEYS.SHIFT && ComposingState.get()) {
        refreshCandidates();
      }

      // simple-keyboard's internal caret tracking doesn't reliably update
      // when handleButtonClicked is called from a module override.
      // Explicitly sync the caret to the end of the current input.
      keyboard.setCaretPosition(keyboard.getInput().length);
    };

    keyboard.clearInput = function () {
      keyboard.setInput('');
      flushCandidates();
    };
  });
};

export default candidatesModule;
